import {
  ActivitiesRow,
  SectionsRow,
} from "@outschool/db-queries/dist/generated/types";
import * as FilestackUrls from "@outschool/filestack-urls";
import { Leader } from "@outschool/gql-backend-generated";
import {
  addParamsToUrl,
  leaderUrl,
  objectToQueryString,
  parentPath,
} from "@outschool/routes";

import closeHtml from "../../public/close.html";
import {
  EnrolledClassActivity,
  EnrolledClassSection,
} from "../client/components/enrolled/EnrolledClassTypes";
import * as Env from "./Env";
import * as Routes from "./Routes";

function sectionOrActivityUrl(
  activity: { uid: string; slug_id?: string; title?: string | null },
  section?: EnrolledClassSection | SectionsRow,
  referrer?: { slug_id: string },
  locale?: string | null,
): string {
  return addParamsToUrl(
    Routes.sectionOrActivityUrl(
      activity,
      section,
      undefined,
      section ? true : undefined,
      locale,
    ),
    {
      usid: referrer ? referrer.slug_id : undefined,
      signup: true,
    },
  );
}

/** Create url for Facebook's share dialog from an activity. Optionally, it can
 *  take a section, a referring user, a quote, and a tracking campaign prefix.
 *
 *  This url has the ability to show the option to share to Facebook groups. FYI
 *
 *  See https://developers.facebook.com/docs/sharing/reference/share-dialog
 */
export function facebookActivityShareUrl(
  activity: { uid: string; slug_id?: string; title?: string | null },
  section?: EnrolledClassSection | SectionsRow,
  referrer?: { slug_id: string },
  quote?: string,
  campaignPrefix?: string,
  locale?: string | null,
): string {
  const shareUrl = "https://www.facebook.com/dialog/share?";
  const params = {
    app_id: Env.FACEBOOK_APP_ID,
    display: "page",
    href: addParamsToUrl(
      sectionOrActivityUrl(activity, section, referrer, locale),
      {
        utm_campaign: `${campaignPrefix || "share_activity"}_facebook`,
      },
    ),
    redirect_uri: closeHtml,
    quote: quote,
  };

  return (
    // hashtag should be prefixed with # and not urlescaped
    shareUrl + objectToQueryString(params) + "&hashtag=#outschool"
  );
}

/** Create url for Facebook's send dialog from an activity. Optionally, it can
 *  take a section, a referring user, and a tracking campaign prefix.
 *
 *  See https://developers.facebook.com/docs/sharing/reference/send-dialog
 */
export function facebookActivitySendUrl(
  activity: { uid: string; slug_id?: string; title?: string },
  section?: EnrolledClassSection | SectionsRow,
  referrer?: { slug_id: string },
  campaignPrefix?: string,
): string {
  const shareUrl = "https://www.facebook.com/dialog/send?";
  const params = {
    app_id: Env.FACEBOOK_APP_ID,
    link: addParamsToUrl(sectionOrActivityUrl(activity, section, referrer), {
      utm_campaign: `${campaignPrefix || "share_activity"}_facebook`,
    }),
    redirect_uri: closeHtml,
  };

  return shareUrl + objectToQueryString(params);
}

// See https://developers.facebook.com/docs/sharing/reference/share-dialog
export function facebookGeneralShareUrl(url: string): string {
  const shareUrl = "https://www.facebook.com/dialog/share?";
  const params = {
    app_id: Env.FACEBOOK_APP_ID,
    display: "page",
    href: url,
    redirect_uri: closeHtml,
  };

  return (
    // hashtag should be prefixed with # and not urlescaped
    shareUrl + objectToQueryString(params) + "&hashtag=#outschool"
  );
}

// See https://developers.facebook.com/docs/sharing/reference/send-dialog
export function facebookGeneralSendUrl(url: string): string {
  const shareUrl = "https://www.facebook.com/dialog/send?";
  const params = {
    app_id: Env.FACEBOOK_APP_ID,
    link: url,
    redirect_uri: closeHtml,
  };

  return shareUrl + objectToQueryString(params);
}

// See https://developers.facebook.com/docs/sharing/reference/share-dialog
export function facebookLeaderShareUrl(
  leader: { uid: string; leader_link?: string | null },
  referrer?: { slug_id: string },
): string {
  const shareUrl = "https://www.facebook.com/dialog/share";
  const params = {
    app_id: Env.FACEBOOK_APP_ID,
    display: "page",
    href: addParamsToUrl(leaderUrl(leader.uid, leader.leader_link), {
      signup: true,
      utm_campaign: "share_leader_facebook",
      usid: referrer ? referrer.slug_id : undefined,
    }),
    redirect_uri: closeHtml,
  };

  return addParamsToUrl(shareUrl, params);
}

// See https://developers.facebook.com/docs/sharing/reference/share-dialog
export function facebookParentShareUrl(
  parent: { uid: string },
  referrer?: { slug_id: string },
): string {
  const shareUrl = "https://www.facebook.com/dialog/share";
  const params = {
    app_id: Env.FACEBOOK_APP_ID,
    display: "page",
    href: addParamsToUrl(parentPath(parent.uid), {
      signup: true,
      utm_campaign: "share_parent_facebook",
      usid: referrer ? referrer.slug_id : undefined,
    }),
    redirect_uri: closeHtml,
  };

  return addParamsToUrl(shareUrl, params);
}

export function pinterestLeaderUrl(leader: Leader): string {
  var shareUrl = "https://www.pinterest.com/pin/create/button?";
  var params = {
    url: addParamsToUrl(leaderUrl(leader.uid, leader.leader_link), {
      utm_campaign: "share_leader_pinterest",
    }),
    media: FilestackUrls.headshotImageUrl(leader.photo),
    description: leader.name + " - " + leader.details.headline,
  };

  return shareUrl + objectToQueryString(params);
}

export function twitterActivityUrl(
  activity: EnrolledClassActivity | ActivitiesRow,
  section?: EnrolledClassSection | SectionsRow,
  referrer?: { slug_id: string },
): string {
  const shareUrl = "https://twitter.com/intent/tweet?";
  const params = {
    url: addParamsToUrl(sectionOrActivityUrl(activity, section, referrer), {
      utm_campaign: "share_activity_twitter",
    }),
    text: "Check out this class for kids: " + activity.title?.substring(0, 90),
    hashtags: "outschool,lovelearning",
  };

  return shareUrl + objectToQueryString(params);
}

export function twitterLeaderUrl(leader: Leader): string {
  var shareUrl = "https://twitter.com/intent/tweet?";
  var params = {
    url: addParamsToUrl(leaderUrl(leader.uid, leader.leader_link), {
      utm_campaign: "share_leader_twitter",
    }),
    text: leader.name + " - teacher profile and classes on Outschool",
    hashtags: "outschool,lovelearning",
  };

  return shareUrl + objectToQueryString(params);
}
